import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errText } from "@/lib/api";
import { fmtDuration, fmtDateTime } from "@/lib/time";
import { MaintenanceScreen } from "../MaintenanceScreen";
import { LEADS } from "./ScheduleDialog";
import { SeeMore, useSeeMore } from "./SeeMore";

const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";
const card = "border border-black/15 bg-white p-6";

export const VisitorSettings = ({ settings, onSaved }) => {
  const [f, setF] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  useEffect(() => { setF(settings); return undefined; }, [settings]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/maintenance/settings", { title: f.title, message: f.message, banner_lead_minutes: Number(f.banner_lead_minutes), show_countdown: !!f.show_countdown }); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const sample = { mode: "manual", title: f.title, message: f.message, show_countdown: f.show_countdown, ends_at: new Date(Date.now() + 5400000).toISOString() };
  return (
    <section className={card} data-testid="maintenance-visitor-settings">
      <h2 className="font-display text-xl font-extrabold tracking-tight">Maintenance page</h2>
      <p className="mt-1 text-sm text-[#57534E]">What visitors see while the site is offline.</p>
      <div className="mt-5 space-y-4">
        <label className="block"><span className="mb-1.5 block text-sm font-bold">Headline</span><input value={f.title} maxLength={120} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} data-testid="settings-title" /></label>
        <label className="block"><span className="mb-1.5 block text-sm font-bold">Message</span><textarea rows={3} value={f.message} maxLength={1000} onChange={(e) => setF({ ...f, message: e.target.value })} className={field} data-testid="settings-message" /></label>
        <label className="block"><span className="mb-1.5 block text-sm font-bold">Default warning banner before scheduled maintenance</span>
          <select value={f.banner_lead_minutes} onChange={(e) => setF({ ...f, banner_lead_minutes: e.target.value })} className={field} data-testid="settings-lead">
            {LEADS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center justify-between gap-3 text-sm font-bold">Show a live countdown<Switch checked={!!f.show_countdown} onCheckedChange={(v) => setF({ ...f, show_countdown: v })} data-testid="settings-countdown" /></label>
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={save} disabled={busy} className="flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white hover:bg-[#E62314]" data-testid="settings-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
          <button onClick={() => setPreview(true)} className="flex items-center gap-2 border border-black/20 px-5 py-3 text-sm font-semibold hover:border-[#0A0A0A]" data-testid="settings-preview"><Eye className="h-4 w-4" />Preview page</button>
        </div>
      </div>
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-none border-0 p-0" data-testid="settings-preview-dialog">
          <DialogTitle className="sr-only">Maintenance page preview</DialogTitle>
          <MaintenanceScreen status={sample} embedded />
        </DialogContent>
      </Dialog>
    </section>
  );
};

export const PreviewLink = ({ token, onChanged }) => {
  const url = `${window.location.origin}/?preview=${token}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch (e) { toast.error("Couldn't copy. Select the link and copy it"); } };
  const rotate = async () => {
    if (!window.confirm("Create a new link? The old link will stop working.")) return;
    try { await api.post("/maintenance/preview-token"); toast.success("New link created"); onChanged(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <section className={card} data-testid="maintenance-preview-link">
      <h2 className="font-display text-xl font-extrabold tracking-tight">Private preview link</h2>
      <p className="mt-1 text-sm text-[#57534E]">Anyone with this link can see the site while it's offline, without logging in.</p>
      <input readOnly value={url} onFocus={(e) => e.target.select()} className={`${field} mt-4 font-mono text-xs`} data-testid="preview-link-url" />
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={copy} className="flex items-center gap-2 bg-[#0A0A0A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#E62314]" data-testid="preview-link-copy"><Copy className="h-4 w-4" />Copy link</button>
        <button onClick={rotate} className="flex items-center gap-2 border border-black/20 px-4 py-2.5 text-sm font-semibold hover:border-[#0A0A0A]" data-testid="preview-link-rotate"><RefreshCw className="h-4 w-4" />New link</button>
      </div>
    </section>
  );
};

export const HistoryLog = ({ log, onChanged }) => {
  const now = Date.now();
  const page = useSeeMore(log);
  const isDone = (e) => e.ended_at && new Date(e.ended_at).getTime() <= now;
  const remove = async (e) => {
    try { await api.delete(`/maintenance/log/${e.id}`); toast.success("Entry deleted"); onChanged(); } catch (err) { toast.error(errText(err)); }
  };
  const clear = async () => {
    if (!window.confirm("Delete all finished downtime history? This can't be undone.")) return;
    try { const r = await api.delete("/maintenance/log"); toast.success(`Deleted ${r.data?.deleted ?? 0} entr${r.data?.deleted === 1 ? "y" : "ies"}`); onChanged(); } catch (err) { toast.error(errText(err)); }
  };
  return (
    <section data-testid="maintenance-history">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">History</h2>
        {log.some(isDone) && <button onClick={clear} data-testid="history-clear" className="flex items-center gap-1.5 border border-black/20 bg-white px-3.5 py-2 text-xs font-semibold hover:border-[#E62314] hover:text-[#E62314]"><Trash2 className="h-3.5 w-3.5" />Clear history</button>}
      </div>
      <div className="mt-4 border border-black/15 bg-white">
        {!log.length ? <p className="p-8 text-center text-sm text-[#57534E]" data-testid="maintenance-history-empty">No downtime yet.</p> : (
          <>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead className="border-b border-black/10 text-xs uppercase tracking-wider text-[#57534E]"><tr><th className="px-5 py-3">Type</th><th className="px-5 py-3">Details</th><th className="px-5 py-3">Started</th><th className="px-5 py-3">Length</th><th className="px-5 py-3">By</th><th className="px-3 py-3"><span className="sr-only">Delete</span></th></tr></thead>
            <tbody>
              {page.shown.map((e) => {
                const ended = isDone(e);
                return (
                  <tr key={e.id} className="border-b border-black/5" data-testid={`history-${e.id}`}>
                    <td className="px-5 py-3"><span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${e.kind === "manual" ? "bg-[#0A0A0A] text-white" : e.kind === "launch" ? "bg-[#16A34A] text-white" : "bg-[#F3F2EF]"}`}>{e.kind === "manual" ? "Instant" : e.kind === "launch" ? "Launch" : "Scheduled"}</span></td>
                    <td className="px-5 py-3">{e.reason || e.title}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{fmtDateTime(e.started_at)}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{e.kind === "launch" ? "—" : ended ? fmtDuration(new Date(e.ended_at) - new Date(e.started_at)) : <span className="font-semibold text-[#E62314]">In progress</span>}</td>
                    <td className="px-5 py-3 text-[#57534E]">{e.by}</td>
                    <td className="px-3 py-3 text-right">{ended && <button onClick={() => remove(e)} aria-label="Delete entry" data-testid={`history-${e.id}-delete`} className="rounded p-1.5 text-[#57534E] hover:bg-black/5 hover:text-[#E62314]"><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <SeeMore {...page} testId="history" />
          </>
        )}
      </div>
    </section>
  );
};
